/**
 * JPEG-2000 Part 1 decoder bridge — drives a pool of Web Workers
 * (see j2kWorker.ts), each of which loads the Zig-compiled WASM
 * module (`/public/j2k.wasm`, built by `zig build j2k-wasm` from
 * src/lib/j2k/*.zig) and decodes codestream bytes into RGBA frames.
 *
 * Decoder is byte-exact against OpenJPEG 2.5.3 — see
 * project_j2k_decoder.md memory and the test suite in
 * src/lib/j2k/j2k.zig (27 tests, both fixtures byte-identical).
 */

export type J2KFrame = {
  width:  number;
  height: number;
  rgba:   Uint8ClampedArray;
};

// Matches the hardware-thread count we've seen in typical pathologist
// laptops (4-core). Keeping the pool modest keeps memory pressure
// under control — each worker holds a WASM instance with its own
// 16-32 MiB arena.
const WORKER_POOL_SIZE = 4;

let workers: Worker[] | null = null;
const pending = new Map<number, (f: J2KFrame | Error) => void>();
let nextId = 1;
let rr = 0;

function ensureWorkers(): Worker[] {
  if (workers) return workers;
  const pool: Worker[] = [];
  for (let i = 0; i < WORKER_POOL_SIZE; i++) {
    const w = new Worker(
      new URL("./j2kWorker.ts", import.meta.url),
      { type: "module" },
    );
    w.onmessage = (e: MessageEvent) => {
      const { id, ok, width, height, rgba, error } = e.data;
      const resolve = pending.get(id);
      if (!resolve) return;
      pending.delete(id);
      if (ok) resolve({ width, height, rgba });
      else    resolve(new Error(error || "decode failed"));
    };
    w.onerror = (err) => {
      console.error("[j2kDecoder] worker error", err);
    };
    pool.push(w);
  }
  workers = pool;
  return pool;
}

/** Decode a JPEG-2000 codestream to an RGBA frame on a worker
 *  thread. Transfers the codestream bytes to the worker (zero-copy)
 *  and receives an RGBA Uint8ClampedArray back, also transferred. */
export async function decodeJ2K(stream: Uint8Array): Promise<J2KFrame> {
  const pool = ensureWorkers();
  const w = pool[rr % pool.length];
  rr++;
  const id = nextId++;
  // Copy into a fresh ArrayBuffer so we can transfer it without
  // neutering the caller's backing buffer (the bytes may be a view
  // into a multipart/related response buffer the caller still needs).
  const owned = new Uint8Array(stream.byteLength);
  owned.set(stream);
  return new Promise<J2KFrame>((resolve, reject) => {
    pending.set(id, (result) => {
      if (result instanceof Error) reject(result);
      else resolve(result);
    });
    w.postMessage({ id, bytes: owned.buffer }, [owned.buffer]);
  });
}

/** Returns true iff the given DICOMweb part Content-Type indicates
 *  a JPEG-2000 codestream that requires our WASM decoder. */
export function isJ2KMediaType(ct: string): boolean {
  const base = ct.split(";")[0].trim().toLowerCase();
  return (
    base === "image/jp2" ||
    base === "image/jpx" ||
    base === "image/jpeg2000" ||
    base === "image/j2c" ||
    base === "image/j2k"
  );
}
